const React = require("react");
const interopDefault = (moduleValue) => moduleValue?.default || moduleValue;
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const { PageHeader, Button, ResourceState, StatusBadge } = require("../components/ui");
const { isSupplierOrderPaid } = require("../orderPaymentUI");
const { CJOrderCheckoutModal } = require("../components/CJOrderCheckoutModal");
const h = React.createElement;

const PLACEHOLDER_PRODUCT_IMG = "https://placehold.co/80x80/e7f4f6/007399?text=CJ";

const money = (value, currency = "USD") => `${Number(value || 0).toFixed(2)} ${currency}`;
const display = (value) => value === undefined || value === null || value === "" ? "—" : String(value);
const OrderCard = ({ order, t, api, busy, onCreate, onTrack, onReload, paymentStatusLabels }) => {
  const [showCheckout, setShowCheckout] = React.useState(false);
  const paid = isSupplierOrderPaid(order);
  const orderId = order.orderId?._id || order.orderId;
  const createdAt = order.orderId?.createdAt ? new Date(order.orderId.createdAt).toLocaleDateString() : "";
  const primaryItem = order.items?.[0] || {};
  const selectedVariant = primaryItem.selectedProviderVariant || {};
  const discountedPrice = Number(primaryItem.unitPrice || 0) * (100 - Number(primaryItem.salePrice || 0)) / 100;
  return h(React.Fragment, null,
    h("article", { className: "cjds-order-card" },
    h("div", { className: "cjds-order-hero" },
      h("div", { className: "cjds-order-hero-media" },
        h("img", { src: selectedVariant.image || primaryItem.image || PLACEHOLDER_PRODUCT_IMG, alt: selectedVariant.name || primaryItem.title || "" }),
        order.platformId?.logo ? h("span", { className: "cjds-order-hero-platform" },
          h("img", { src: order.platformId.logo, alt: order.platformId?.name || "CJ" })
        ) : null
      ),
      h("div", { className: "cjds-order-hero-main" },
        h("div", { className: "cjds-order-hero-topline" },
          h("span", { className: "cjds-order-eyebrow" }, `${order.platformId?.name || "CJ Dropshipping"} · #${String(orderId || "").slice(-10)}`),
          h("span", { className: "cjds-muted cjds-order-date" }, [createdAt, order.supplierId?.name].filter(Boolean).join(" — "))
        ),
        h("h3", { className: "cjds-order-hero-title" }, selectedVariant.name || primaryItem.title || t("h_cj_untitled_product", "Product")),
        h("div", { className: "cjds-order-hero-price" },
          h("strong", null, money(discountedPrice)),
          Number(primaryItem.salePrice || 0) > 0 ? h("del", null, money(primaryItem.unitPrice)) : null,
          Number(primaryItem.salePrice || 0) > 0 ? h("span", { className: "cjds-order-discount" }, `-${Number(primaryItem.salePrice)}%`) : null
        ),
        h("div", { className: "cjds-order-hero-amount" },
          h("span", null, t("h_cj_elakay_total", "Montant Elakay")),
          h("strong", null, money(order.orderId?.amountTotal))
        ),
        h("div", { className: "cjds-order-hero-status" },
          h(StatusBadge, { status: order.status, t }),
          h("span", null, paymentStatusLabels[order.paymentStatus] || display(order.paymentStatus))
        )
      ),
      // Unpaid supplier order -> "Checkout" (carrier review + Pay on CJ live
      // inside it); paid -> "View Order". Opening a card never calls CJ.
      h("div", { className: "cjds-order-hero-actions" },
        paid
          ? h(Button, { variant: "secondary", className: "cjds-order-view-btn", onClick: () => setShowCheckout(true), "aria-haspopup": "dialog" },
            t("h_cj_view_order", "Voir la commande"))
          : h(Button, { className: "cjds-order-checkout-btn", disabled: busy, onClick: () => setShowCheckout(true), "aria-haspopup": "dialog" },
            t("h_cjck_checkout", "Checkout"))
      )
    )),
    h(CJOrderCheckoutModal, {
      show: showCheckout, onHide: () => setShowCheckout(false), order, api, t, busy,
      onCreate, onTrack, onPaid: onReload,
    })
  );
};

const DropshippingOrders = () => {
  const { api, t } = useCJDropshipping();
  const resource = useDropshippingResource(() => api.getOrders({ page: 1, limit: 50 }), [api]);
  const [busyId, setBusyId] = React.useState("");
  const [error, setError] = React.useState("");
  const orders = resource.data?.orders || [];
  const track = async (order) => {
    setBusyId(order._id); setError("");
    // logId (l'id du DropshippingOrderLog lui-même) désambiguïse quand cette
    // commande Elakay a deux commandes CJ pour ce vendeur (lot "default" +
    // lot "override" -- voir la puce Auto-pay produit).
    try { await api.getTracking(order.orderId?._id || order.orderId, order._id); await resource.reload(); }
    catch (trackingError) { setError(trackingError.message); }
    finally { setBusyId(""); }
  };
  const create = async (order) => {
    const orderId = order.orderId?._id || order.orderId;
    if (!orderId || !window.confirm(t("h_cj_confirm_send_order", "Send this paid order to CJ Dropshipping? Supplier charges may apply."))) return;
    setBusyId(order._id); setError("");
    try { await api.createSupplierOrder(orderId); await resource.reload(); }
    catch (requestError) { setError(requestError.message); }
    finally { setBusyId(""); }
  };
  const paymentStatusLabels = {
    not_required: t("h_cj_payment_status_not_required", "N/A"),
    pending: t("h_cj_payment_status_pending", "Pending"),
    auto_payment_processing: t("h_cj_payment_status_processing", "Processing"),
    paid: t("h_cj_payment_status_paid", "Paid"),
    manual_payment_required: t("h_cj_payment_status_manual_required", "Manual payment required"),
    failed: t("h_cj_payment_status_failed", "Failed"),
    refunded: t("h_cj_payment_status_refunded", "Refunded"),
  };
  return h(React.Fragment, null,
    h(PageHeader, {
      title: t("h_cj_orders_title", "Dropshipping orders"),
      description: t("h_cj_orders_desc", "Supplier order status and tracking returned by CJ."),
      action: h(Button, { variant: "secondary", disabled: resource.loading || Boolean(busyId), onClick: resource.reload }, t("h_cj_refresh", "Refresh")),
    }),
    error ? h("div", { className: "cjds-alert", role: "alert" }, error) : null,
    h(ResourceState, { loading: resource.loading, error: resource.error, empty: !orders.length, onRetry: resource.reload, t },
      h("div", { className: "cjds-order-list" }, ...orders.map((order) => h(OrderCard, {
        key: order._id,
        order,
        t,
        busy: busyId === order._id,
        api,
        onCreate: create,
        onTrack: track,
        onReload: resource.reload,
        paymentStatusLabels,
      })))
    )
  );
};
module.exports = { DropshippingOrders };
